import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Spinner,
  Flex,
  Badge,
} from "@chakra-ui/react";
import { FaFileAlt, FaTrash, FaTimes, FaClipboardList, FaCogs, FaLayerGroup } from "react-icons/fa";
import DataTableComponent from "containers/analysis-plan/DataTableComponent";
import { NodeInsights } from "containers/Interfaces";
import { ScenarioResultProto, ScenariosProcessingOutput } from "build-ts/schedule";
import ReactMarkdown from "react-markdown";

interface ArtifactsProps {
  selectedNodesToShowTables: {node: NodeInsights, loading: boolean}[];
  isLoadingTableData: boolean;
  onClearAllTables: () => void;
  onClosePanel: () => void;
  onRemoveTable: (nodeId: string) => void;
  selectedFindingsIds?: number[];
  onRemoveFindings?: (scenarioId: number) => void;
  scenarioResults?: ScenarioResultProto[];
  showSynthesis?: boolean;
  showContextualization?: boolean;
  synthesisResult?: ScenariosProcessingOutput;
  contextualisationResult?: ScenariosProcessingOutput;
  onRemoveSynthesis?: () => void;
  onRemoveContextualization?: () => void;
  artifactOrder?: string[];
}

const Artifacts: React.FC<ArtifactsProps> = ({
  selectedNodesToShowTables,
  isLoadingTableData,
  onClearAllTables,
  onClosePanel,
  onRemoveTable,
  selectedFindingsIds = [],
  onRemoveFindings,
  scenarioResults = [],
  showSynthesis = false,
  showContextualization = false,
  synthesisResult,
  contextualisationResult,
  onRemoveSynthesis,
  onRemoveContextualization,
  artifactOrder = [],
}) => {
  const totalItems = selectedNodesToShowTables.length + selectedFindingsIds.length + 
    (showSynthesis ? 1 : 0) + (showContextualization ? 1 : 0);

  const renderFindingsItem = (scenarioId: number) => {
    // Find the scenario result by ID
    const scenarioResult = scenarioResults.find(result => result.scenario_id === scenarioId);
    
    if (!scenarioResult) {
      return null;
    }

    return (
      <Box p={4}>
        <HStack justifyContent="space-between" mb={3}>
          <HStack>
            <Icon as={FaClipboardList} boxSize={3} color="purple.500" />
            <Badge colorScheme="purple" size="xs">
              {scenarioResult.issue || `Scenario ${scenarioId}`} Findings
            </Badge>
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "red.500", bg: "red.50" }}
            onClick={() => onRemoveFindings?.(scenarioId)}
          >
            <Icon as={FaTimes} boxSize="3" />
          </Button>
        </HStack>
        <Box
          p={3}
          bg="purple.50"
          borderRadius="md"
          border="1px"
          borderColor="purple.200"
          maxHeight="400px"
          overflowY="auto"
        >
          <Text fontSize="sm" color="purple.800" whiteSpace="pre-wrap" textAlign="left" m={1}>
            <ReactMarkdown>{scenarioResult.findings || "No findings available"}</ReactMarkdown>
          </Text>
        </Box>
      </Box>
    );
  };

  const renderSynthesisItem = () => {
    if (!synthesisResult || !synthesisResult.output_string) {
      return null;
    }

    return (
      <Box p={4}>
        <HStack justifyContent="space-between" mb={3}>
          <HStack>
            <Icon as={FaCogs} boxSize={3} color="blue.500" />
            <Badge colorScheme="blue" size="xs">
              Synthesis Output
            </Badge>
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "red.500", bg: "red.50" }}
            onClick={() => onRemoveSynthesis?.()}
          >
            <Icon as={FaTimes} boxSize="3" />
          </Button>
        </HStack>
        <Box
          p={3}
          bg="blue.50"
          borderRadius="md"
          border="1px"
          borderColor="blue.200"
          maxHeight="400px"
          overflowY="auto"
        >
          <Text fontSize="sm" color="blue.800" whiteSpace="pre-wrap" textAlign="left" m={1}>
            <ReactMarkdown>{synthesisResult.output_string}</ReactMarkdown>
          </Text>
        </Box>
      </Box>
    );
  };

  const renderContextualizationItem = () => {
    if (!contextualisationResult || !contextualisationResult.output_string) {
      return null;
    }

    return (
      <Box p={4}>
        <HStack justifyContent="space-between" mb={3}>
          <HStack>
            <Icon as={FaLayerGroup} boxSize={3} color="orange.500" />
            <Badge colorScheme="orange" size="xs">
              Contextualization Output
            </Badge>
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "red.500", bg: "red.50" }}
            onClick={() => onRemoveContextualization?.()}
          >
            <Icon as={FaTimes} boxSize="3" />
          </Button>
        </HStack>
        <Box
          p={3}
          bg="orange.50"
          borderRadius="md"
          border="1px"
          borderColor="orange.200"
          maxHeight="400px"
          overflowY="auto"
        >
          <Text fontSize="sm" color="orange.800" whiteSpace="pre-wrap" textAlign="left" m={1}>
            <ReactMarkdown>{contextualisationResult.output_string}</ReactMarkdown>
          </Text>
        </Box>
      </Box>
    );
  };

  // Function to render artifacts in the correct order
  const renderArtifactsInOrder = () => {
    const artifactComponents: JSX.Element[] = [];

    // Process each artifact in the order they were clicked (most recent first)
    artifactOrder.forEach((artifactId) => {
      if (artifactId === "synthesis" && showSynthesis) {
        artifactComponents.push(
          <Box
            key="synthesis"
            width="100%"
            bg="white"
            borderWidth={1}
            borderColor="gray.300"
            borderRadius="md"
            boxShadow="sm"
            overflow="hidden"
          >
            {renderSynthesisItem()}
          </Box>
        );
      } else if (artifactId === "contextualization" && showContextualization) {
        artifactComponents.push(
          <Box
            key="contextualization"
            width="100%"
            bg="white"
            borderWidth={1}
            borderColor="gray.300"
            borderRadius="md"
            boxShadow="sm"
            overflow="hidden"
          >
            {renderContextualizationItem()}
          </Box>
        );
      } else if (artifactId.startsWith("findings-")) {
        const scenarioId = parseInt(artifactId.replace("findings-", ""));
        if (selectedFindingsIds.includes(scenarioId)) {
          artifactComponents.push(
            <Box
              key={scenarioId}
              width="100%"
              bg="white"
              borderWidth={1}
              borderColor="gray.300"
              borderRadius="md"
              boxShadow="sm"
              overflow="hidden"
            >
              {renderFindingsItem(scenarioId)}
            </Box>
          );
        }
      } else if (artifactId.startsWith("table-")) {
        const nodeId = artifactId.replace("table-", "");
        const tableItem = selectedNodesToShowTables.find(
          (selected) => selected.node.current_node.id === nodeId
        );
        if (tableItem) {
          artifactComponents.push(
            <Box
              key={nodeId}
              width="100%"
              bg="white"
              borderWidth={1}
              borderColor="gray.300"
              borderRadius="md"
              boxShadow="sm"
              overflow="hidden"
            >
              <DataTableComponent
                observationData={tableItem.node}
                onCloseTable={() => onRemoveTable(nodeId)}
              />
            </Box>
          );
        }
      }
    });

    return artifactComponents;
  };

  return (
    <Box height="95%" display="flex" flexDirection="column" bg="white">
      {/* Header */}
      <HStack
        justifyContent="space-between"
        p={3}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="gray.50"
      >
        <HStack>
          <Icon as={FaFileAlt} boxSize={4} color="purple.500" />
          <Text fontSize="md" fontWeight="600" color="gray.700" m={0}>
            Artifacts
          </Text>
        </HStack>
        <HStack spacing={2}>
          {/* Clear All Button */}
          {totalItems > 0 && (
            <Button
              leftIcon={<Icon as={FaTrash} boxSize="3" />}
              size="xs"
              colorScheme="red"
              variant="ghost"
              color="gray.500"
              _hover={{ color: "red.500", bg: "red.50" }}
              onClick={onClearAllTables}
              fontSize="xs"
            >
              Clear All
            </Button>
          )}
          {/* Close Panel Button */}
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "gray.700", bg: "gray.100" }}
            onClick={onClosePanel}
            p={1}
          >
            <Icon as={FaTimes} boxSize="3" />
          </Button>
        </HStack>
      </HStack>

      {/* Loading Indicator */}
      {isLoadingTableData && (
        <Box
          p={2}
          position="absolute"
          top="60px"
          right="20px"
          zIndex="10"
          bg="green.100"
          borderRadius={3}
          boxShadow="md"
        >
          <Flex alignItems="center" justifyContent="center">
            <HStack>
              <Spinner size="sm" color="green.600" thickness="2px" />
              <Text mb={0} fontSize="xs" fontWeight="semibold" color="green.700">
                Fetching data...
              </Text>
            </HStack>
          </Flex>
        </Box>
      )}

      {/* Content */}
      <Box
        flex="1"
        overflowY="auto"
        p={3}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#A0AEC0",
          },
        }}
      >
        {totalItems === 0 && !isLoadingTableData ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={6}
          >
            <Box textAlign="center">
              <Icon as={FaFileAlt} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.600" fontSize="lg" mb={2}>
                No Artifacts
              </Text>
              <Text fontSize="sm" color="gray.500">
                Click on data tables, findings, or outputs in the reasoning tree to view them here
              </Text>
            </Box>
          </Box>
        ) : (
          <VStack spacing={3} width="100%">
            {renderArtifactsInOrder()}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default Artifacts;